import React, { memo, useEffect, useMemo, useState, useRef, useContext, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { List } from 'react-window';
import { Button, Icon } from '@iq/react-components';
import Tippy from '@tippyjs/react/headless';
import AppContext from '../../../AppContext';
import {
  useActiveListEvents,
  useHoverContext,
  useSetEventListOrdering,
  useSelectedEventType,
} from './EventTimelineState';
import Indicator from './Indicator';
import UserEventStatus from './UserEventStatus';
import { useIsLoading } from '../../LoadingLayer';
import { useScrollBlock } from '../../../utils';
import { formatSiteDatetime } from '../../../datetimeUtils';
import config from '../../../config';
import { getTimezone } from '../../../bundles/application';
import { getLimitedUsers } from '../../../bundles/ad';
import { getStaticComponents } from '../../../bundles/components';
import {
  EVENT_TYPE_USER,
  EVENT_TYPE_DTMP,
  EVENT_TYPE_MACH,
  STATELESS_EVENTS,
} from '../../../constants';
import { setImportOrder } from '../../../bundles/events';
import { getComponentNameForDtmpEvent } from './utils';

const DurationArrow = ({ down }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="8"
    height="7"
    viewBox="0 0 8 7"
    style={{
      transform: down ? 'rotate(180deg)' : '',
    }}
  >
    <rect
      width="8"
      height="1"
      fill="currentcolor"
    />
    <path
      d="M4.35355 1.64645C4.15829 1.45118 3.84171 1.45118 3.64645 1.64645L0.464466 4.82843C0.269204 5.02369 0.269204 5.34027 0.464466 5.53553C0.659728 5.7308 0.976311 5.7308 1.17157 5.53553L4 2.70711L6.82843 5.53553C7.02369 5.7308 7.34027 5.7308 7.53553 5.53553C7.7308 5.34027 7.7308 5.02369 7.53553 4.82843L4.35355 1.64645ZM4.5 7L4.5 2L3.5 2L3.5 7L4.5 7Z"
      fill="currentcolor"
    />
  </svg>
);

const Tag = ({ children }) => <div className="event-label">{children}</div>;

export const OPTIONAL_MACH_EVENT_COLUMNS = [
  { id: 'label', title: 'Label' },
  { id: 'active', title: 'Active system' },
  { id: 'sender', title: 'Sender' },
  { id: 'pointId', title: 'Point ID' },
];
export const OPTIONAL_USER_EVENT_COLUMNS = [
  { id: 'activity', title: 'Activity' },
  { id: 'state', title: 'Status' },
  { id: 'assignedTo', title: 'Assignee' },
  { id: 'actions', title: 'Actions' },
];
export const OPTIONAL_DTMP_EVENT_COLUMNS = [
  { id: 'messageType', title: 'Message Type' },
  { id: 'algorithmName', title: 'Algorithm Name' },
  { id: 'algorithmType', title: 'Algorithm Type' },
  { id: 'additionalIdentifier', title: 'Identifier' },
  { id: 'deviatingReferenceDesignation', title: 'Component' },
  { id: 'recommendation', title: 'Recommendation' },
];

const EventListItem = memo(
  ({
    index,
    style,
    onOpenEvent,
    listEvents,
    next,
    limit,
    time,
    height,
    parentWidth,
    format,
    timezone,
    listColumns,
    allUserEventTypes,
    showMachRemoteSourceColumn,
  }) => {
    const item = listEvents[index];

    const [isHovering, setHovering] = useHoverContext(item?.id);
    const { theme } = useContext(AppContext);
    const users = useSelector(getLimitedUsers);
    const staticComponents = useSelector(getStaticComponents);
    // Single ref-map keyed by field name, populated via callback refs, so adding a
    // new truncatable/tooltip-able column doesn't require another individual useRef().
    const cellReferences = useRef({});
    const setCellRef = useCallback(
      (key) => (element) => {
        cellReferences.current[key] = element;
      },
      []
    );
    const [hoverStatus, setHoverStatus] = useState({});

    const compareSize = useCallback(() => {
      const updatedHoverStatus = {};
      for (const [key, element] of Object.entries(cellReferences.current)) {
        if (element) {
          updatedHoverStatus[key] = element.scrollWidth > element.clientWidth;
        }
      }
      setHoverStatus(updatedHoverStatus);
    }, []);

    useEffect(() => {
      globalThis.addEventListener('mouseover', compareSize);

      return () => {
        globalThis.removeEventListener('mouseout', compareSize);
      };
    }, [compareSize]);

    if (!item) {
      if (limit) return null;
      return (
        <div
          className="event-timeline-list-item--loading"
          style={{ ...style, height, width: parentWidth }}
          key={index}
        >
          Loading...
        </div>
      );
    }

    const {
      type,
      event: {
        context: { externalData: { severity: externalSeverity } = {} } = {}, // cloud (originalSeverity)
        originalSeverity, // onPremise
        severity,
        active,
        type: userEventType,
        state,
      },
    } = item;

    const start = type === 'user-start';
    const end = type === 'user-end';
    const className = [
      'event-timeline-list-item',
      limit && 'event-timeline-list-item--limited-list',
      severity === 'critical' && 'event-timeline-list-item--critical',
      severity === 'warning' && 'event-timeline-list-item--warning',
      isHovering && 'event-timeline-list-item--hovering',
      onOpenEvent && 'event-timeline-list-item--clickable',
      limit && format === 'narrow' && 'event-timeline-list-item--limited-list-narrow',
    ]
      .filter(Boolean)
      .join(' ');

    let eventType = EVENT_TYPE_USER;
    if (item.type !== EVENT_TYPE_USER) {
      eventType = item.event.messageType ? EVENT_TYPE_DTMP : EVENT_TYPE_MACH;
    }

    const componentName =
      eventType === EVENT_TYPE_DTMP
        ? getComponentNameForDtmpEvent(item.event, staticComponents)
        : '';

    const clickProperties = onOpenEvent && {
      onClick: () =>
        onOpenEvent({
          event: item.event,
          id: item.id,
          type: eventType,
          componentName,
        }),
      onMouseEnter: () => setHovering(true),
      onMouseLeave: () => setHovering(false),
    };

    const withTippy = (child, value, refName) => (
      <Tippy
        disabled={!hoverStatus[refName]}
        placement="bottom"
        offset={[0, 5]}
        onTrigger={() => {
          document.body.classList.add('select-none');
        }}
        onHide={() => {
          document.body.classList.remove('select-none');
        }}
        touch={['hold', 500]}
        render={(attributes) => (
          <div
            id="tooltip"
            className={`tooltip-box theme-${theme}`}
            tabIndex={-1}
            {...attributes}
          >
            {value}
          </div>
        )}
      >
        {child}
      </Tippy>
    );

    const dateCell = (
      <div className="event-timeline-list-item__date">
        {item.type === EVENT_TYPE_MACH
          ? formatSiteDatetime(item.timestamp, timezone, true)
          : formatSiteDatetime(item.timestamp, timezone)}
        <div className="event-timeline-list-item__indicator">
          {start && <DurationArrow />}
          {end && <DurationArrow down />}
        </div>
      </div>
    );

    const isStatelessEvent = STATELESS_EVENTS.includes(userEventType);

    const typeCell = (
      <div className="event-timeline-list-item__type">
        <Indicator
          severity={severity}
          eventState={isStatelessEvent ? 'stateless' : state}
          round={type === EVENT_TYPE_USER}
        />
        {(eventType === EVENT_TYPE_MACH || eventType === EVENT_TYPE_DTMP) &&
          (externalSeverity || originalSeverity || severity)}
        {userEventType &&
          type.includes(EVENT_TYPE_USER) &&
          allUserEventTypes.find((event) => event.id === userEventType)?.name}
      </div>
    );

    const labelCell = (
      <div className="event-timeline-list-item__label">
        {item.event.isFalseAlarm && <Tag>False</Tag>}
      </div>
    );

    const groupCell = (
      <div
        ref={setCellRef('groupCell')}
        className="event-timeline-list-item__group"
      >
        {item.event.remoteSource || item.event.pointGroup}
      </div>
    );

    const messageCell = (
      <div
        ref={setCellRef('messageCell')}
        className="event-timeline-list-item__message"
      >
        {start && '[START] '}
        {end && '[END] '}
        {item.event.message || item.event.text || item.event.name}
      </div>
    );

    const activeCell = (
      <div className="event-timeline-list-item__active">
        {active && (
          <Icon
            icon="he-check"
            size="s"
          />
        )}
      </div>
    );

    const senderCell = (
      <div
        ref={setCellRef('senderCell')}
        className="event-timeline-list-item__sender"
      >
        {item.event.sender}
      </div>
    );

    const pointIdCell = (
      <div
        ref={setCellRef('pointIdCell')}
        className="event-timeline-list-item__pointId"
      >
        {item.event.origin || item.event.pointId}
      </div>
    );

    const activityCell = (
      <div className="event-timeline-list-item__activity">
        {isStatelessEvent ? '' : { true: 'Planned', false: 'Reactive' }[item.event.planned]}
      </div>
    );

    const stateCell = (
      <div className="event-timeline-list-item__state">
        {isStatelessEvent ? '' : item.event.state}
      </div>
    );

    const assigneeName =
      eventType === EVENT_TYPE_USER
        ? users.find((user) => user.id === item.event.assignedTo)?.name || 'Not Assigned'
        : '';
    const assignedToCell = (
      <div
        ref={setCellRef('assignedToCell')}
        className="event-timeline-list-item__assignedTo"
      >
        {assigneeName}
      </div>
    );

    const actionsCell = (
      <div className="event-timeline-list-item__actions">
        {type !== EVENT_TYPE_MACH && <UserEventStatus event={item.event} />}
      </div>
    );

    const messageTypeCell = (
      <div
        ref={setCellRef('messageTypeCell')}
        className="event-timeline-list-item__messageType"
      >
        {item.event.messageType || ''}
      </div>
    );

    const algorithmNameCell = (
      <div
        ref={setCellRef('algorithmNameCell')}
        className="event-timeline-list-item__algorithmName"
      >
        {item.event.algorithmName || ''}
      </div>
    );

    const algorithmTypeCell = (
      <div
        ref={setCellRef('algorithmTypeCell')}
        className="event-timeline-list-item__algorithmType"
      >
        {item.event.algorithmType || ''}
      </div>
    );

    const additionalIdentifierCell = (
      <div
        ref={setCellRef('additionalIdentifierCell')}
        className="event-timeline-list-item__additionalIdentifier"
      >
        {item.event.additionalIdentifier || ''}
      </div>
    );

    const deviatingReferenceDesignationCell = (
      <div
        ref={setCellRef('deviatingReferenceDesignationCell')}
        className="event-timeline-list-item__deviatingReferenceDesignation"
      >
        {componentName}
      </div>
    );

    const recommendationCell = (
      <div
        ref={setCellRef('recommendationCell')}
        className="event-timeline-list-item__recommendation"
      >
        {item.event.recommendation || ''}
      </div>
    );

    if (limit) {
      return (
        <div
          className="limited-list-wrapper"
          style={style}
          key={index}
          {...clickProperties}
        >
          <div className={className}>
            <div className="event-timeline-list-item__upper">
              {typeCell}
              <div className="event-timeline-list-item__date">
                {item.type === EVENT_TYPE_MACH
                  ? formatSiteDatetime(item.timestamp, timezone, true)
                  : formatSiteDatetime(item.timestamp, timezone)}
              </div>
            </div>
            <div className="event-timeline-list-item__lower">
              <div className="event-timeline-list-item__message">
                <div>
                  {start && '[START] '}
                  {end && '[END] '}
                  {item.event.message || item.event.text || item.event.name}
                </div>
                <div className="event-timeline-list-item__indicator">
                  {start && <DurationArrow down={time === 'future'} />}
                  {end && <DurationArrow down={time === 'past'} />}
                </div>
              </div>
              {actionsCell}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div
        className={`${className} ${state}`}
        style={style}
        key={index}
        {...clickProperties}
      >
        {dateCell}
        {typeCell}
        {eventType === EVENT_TYPE_MACH &&
          showMachRemoteSourceColumn &&
          withTippy(groupCell, item.event.remoteSource || item.event.pointGroup, 'groupCell')}
        {withTippy(
          messageCell,
          item.event.message || item.event.text || item.event.name,
          'messageCell'
        )}
        {listColumns.includes('label') && labelCell}
        {listColumns.includes('active') && activeCell}
        {listColumns.includes('sender') && withTippy(senderCell, item.event.sender, 'senderCell')}
        {listColumns.includes('pointId') &&
          withTippy(pointIdCell, item.event.origin || item.event.pointId, 'pointIdCell')}
        {listColumns.includes('activity') && activityCell}
        {listColumns.includes('state') && stateCell}
        {listColumns.includes('assignedTo') &&
          withTippy(assignedToCell, assigneeName, 'assignedToCell')}
        {listColumns.includes('actions') && actionsCell}
        {listColumns.includes('messageType') &&
          withTippy(messageTypeCell, item.event.messageType || '', 'messageTypeCell')}
        {listColumns.includes('algorithmName') &&
          withTippy(algorithmNameCell, item.event.algorithmName || '', 'algorithmNameCell')}
        {listColumns.includes('algorithmType') &&
          withTippy(algorithmTypeCell, item.event.algorithmType || '', 'algorithmTypeCell')}
        {listColumns.includes('additionalIdentifier') &&
          withTippy(
            additionalIdentifierCell,
            item.event.additionalIdentifier || '',
            'additionalIdentifierCell'
          )}
        {listColumns.includes('deviatingReferenceDesignation') &&
          withTippy(
            deviatingReferenceDesignationCell,
            componentName,
            'deviatingReferenceDesignationCell'
          )}
        {listColumns.includes('recommendation') &&
          withTippy(recommendationCell, item.event.recommendation || '', 'recommendationCell')}
      </div>
    );
  }
);

export const EventList = ({
  width,
  height,
  onOpenEvent,
  itemSize = 30,
  limit,
  time,
  format,
  excludeEventId,
  listColumns = [],
  layoutName,
  scoped = true,
  allUserEventTypes,
  onEventsCount,
  showMachRemoteSourceColumn = true,
}) => {
  const dispatch = useDispatch();
  const timezone = useSelector(getTimezone);
  const [ordering, setOrdering] = useSetEventListOrdering();
  const [selectedEventType] = useSelectedEventType();
  const [events, setEvents] = useState([]);
  const {
    events: listEvents = [],
    next,
    hasNextPage,
  } = useActiveListEvents(limit || 100, scoped, Boolean(limit));
  const loading = useIsLoading();
  const [limitListEvents, setLimitListEvents] = useState([]);
  const [blockScroll, allowScroll] = useScrollBlock();
  const eventListReference = useRef();
  const isNextPagePendingReference = useRef(false);
  const lastRequestedEventsLengthReference = useRef(0);

  useEffect(() => {
    // Reset the reference length on any data change to ensure the scroll trigger
    // can correctly compare the new data length against its last known state.
    lastRequestedEventsLengthReference.current = 0;
  }, [events.length]);

  useEffect(() => {
    const filtered = listEvents.filter((event) => event.id !== excludeEventId);
    setEvents(() => filtered);
    onEventsCount?.(filtered.length);
  }, [excludeEventId, listEvents, onEventsCount]);

  useEffect(() => {
    if (!loading) {
      isNextPagePendingReference.current = false;

      if (hasNextPage && events.length === lastRequestedEventsLengthReference.current) {
        lastRequestedEventsLengthReference.current = -1;
      }
    }
  }, [events.length, hasNextPage, loading]);

  const orderIcons = {
    asc: 'he-up',
    desc: 'he-down',
  };

  const filteredColumns = useMemo(
    () =>
      listColumns.filter((col) => {
        if (selectedEventType === EVENT_TYPE_USER) {
          return (
            OPTIONAL_USER_EVENT_COLUMNS.map((optionalCol) => optionalCol.id).includes(col.id) &&
            !(config.onPremise && col.id === 'assignedTo')
          );
        }
        if (selectedEventType === EVENT_TYPE_DTMP) {
          return OPTIONAL_DTMP_EVENT_COLUMNS.map((optionalCol) => optionalCol.id).includes(col.id);
        }
        return OPTIONAL_MACH_EVENT_COLUMNS.map((optionalCol) => optionalCol.id).includes(col.id);
      }),
    [listColumns, selectedEventType, config.onPremise]
  );

  const listHeaders = useMemo(
    () => [
      ['Date', 'date-column'],
      ['Type', 'type-column'],
      ...(selectedEventType === EVENT_TYPE_MACH && showMachRemoteSourceColumn
        ? [['Remote source', 'group-column']]
        : []),
      ['Event name', 'message-column'],
      ...filteredColumns.map((col) => [col.title, `${col.id}-column`]),
      // scroll spacer to prevent y scrollbar from covering the last column
      ['', 'scroll-spacer'],
    ],
    [filteredColumns, selectedEventType, showMachRemoteSourceColumn]
  );

  const toggleOrdering = () => {
    setOrdering(() => Object.keys(orderIcons).find((k) => k !== ordering));
    dispatch(setImportOrder(ordering === 'asc' ? 'desc' : 'asc'));
  };

  // header + border-bottom = 31
  // Cap to actual content height; minimum 1 row keeps the paging sentinel visible.
  const availableHeight = limit
    ? Math.min(height, Math.max(events.length, 1) * itemSize)
    : height - 31;

  useEffect(() => {
    if (limit) {
      setLimitListEvents(() => ({
        listEvents: events,
        onOpenEvent,
        next,
        limit,
        time,
        width,
        height: availableHeight,
        itemSize,
        format,
        timezone,
        allUserEventTypes,
        showMachRemoteSourceColumn,
      }));
    }
  }, [
    onOpenEvent,
    next,
    limit,
    time,
    width,
    availableHeight,
    format,
    timezone,
    allUserEventTypes,
    showMachRemoteSourceColumn,
    events,
    itemSize,
  ]);

  const limitLength = events.length;
  const limitedListLength = limitLength + (hasNextPage ? 1 : 0);
  const eventsLength = events.length + (hasNextPage ? 1 : 0);

  useEffect(() => {
    if (eventListReference.current && limit) {
      eventListReference.current.addEventListener('mouseenter', blockScroll);
      eventListReference.current.addEventListener('mouseleave', allowScroll);
    }
    return () => {
      if (eventListReference.current) {
        eventListReference.current.removeEventListener('mouseenter', blockScroll);
        eventListReference.current.removeEventListener('mouseleave', allowScroll);
      }
      // Guarantee release even if the mouse never left the element before teardown.
      allowScroll();
    };
  }, [blockScroll, allowScroll, limit]);

  /*
   * min width is calculated on the size on the combined size of the following columns,
   * (see EventTimelinePanel.scss where flex basis is set using these numbers):
   * base min-widths (date: 215, type: 150, message: 200)
   * optional min-widthss (active/planned/state/activity: 85, sender/assignedTo/group/pointId: 150)
   */

  const baseWidth = 730;
  const minWidth = useMemo(
    () =>
      baseWidth +
      filteredColumns.length * 165 -
      // remove difference between full width optional and narrow options (165 - 85 = 80)
      (filteredColumns.includes('active') ? 80 : 0) -
      (filteredColumns.includes('activity') ? 80 : 0) -
      (filteredColumns.includes('state') ? 80 : 0) -
      (filteredColumns.includes('actions') ? 80 : 0),
    [filteredColumns]
  );

  const minListWidth = Math.max(width, minWidth);

  // Calculate actual width to use - expand to container width when possible
  const actualWidth = limit ? width : Math.max(minListWidth, width);

  const listItemData = useMemo(() => {
    if (!limit) {
      return {
        listColumns: filteredColumns.map((c) => c.id),
        listEvents: events,
        onOpenEvent,
        next,
        limit,
        time,
        height: availableHeight,
        parentWidth: actualWidth,
        timezone,
        allUserEventTypes,
        showMachRemoteSourceColumn,
      };
    }
    return [];
  }, [
    filteredColumns,
    events,
    onOpenEvent,
    actualWidth,
    next,
    limit,
    time,
    availableHeight,
    timezone,
    allUserEventTypes,
    showMachRemoteSourceColumn,
  ]);

  const handleRowsRendered = useCallback(
    (visibleRows) => {
      const visibleStopIndex = visibleRows?.stopIndex;

      if (
        typeof visibleStopIndex !== 'number' ||
        !hasNextPage ||
        loading ||
        isNextPagePendingReference.current ||
        lastRequestedEventsLengthReference.current === events.length
      ) {
        return;
      }

      const threshold = Math.min(10, Math.max(events.length - 1, 0));
      const triggerIndex = Math.max(events.length - threshold, 0);

      if (visibleStopIndex >= triggerIndex) {
        isNextPagePendingReference.current = true;
        lastRequestedEventsLengthReference.current = events.length;
        next();
      }
    },
    [events.length, hasNextPage, loading, next]
  );

  const eventsList =
    !loading && events.length === 0 ? (
      <div
        className="event-timeline-event-list--empty"
        style={{ height: availableHeight }}
      >
        No events found
      </div>
    ) : (
      <List
        height={availableHeight}
        style={{ width: actualWidth, height: availableHeight }}
        rowCount={limit ? limitedListLength : eventsLength}
        rowHeight={itemSize}
        rowComponent={EventListItem}
        rowProps={limit ? limitListEvents : listItemData}
        onRowsRendered={handleRowsRendered}
        className={`list-view ${layoutName?.replace(' ', '-') || ''} custom-scrollbar`}
      />
    );

  if (limit === 0) {
    return (
      <div
        className="event-timeline-event-list--empty"
        style={{ height: availableHeight }}
      >
        No events
      </div>
    );
  }

  return (
    <div
      ref={eventListReference}
      className={`event-timeline-event-list ${limit ? 'limit-list' : ''}`}
      style={{
        minWidth,
        width: limit ? width : '100%',
        maxWidth: '100%',
      }}
    >
      {!limit && (
        <div className="event-timeline-event-list-headers">
          {listHeaders.map((header, index) => {
            if (index === 0) {
              return (
                <Button
                  key={header[0]}
                  design="text"
                  type="button"
                  className={`list-header ${header[1]}`}
                  onClick={toggleOrdering}
                >
                  {header[0]}
                  <Icon icon={orderIcons[ordering]} />
                </Button>
              );
            }
            return (
              <div
                key={header[0]}
                className={`list-header ${header[1]}`}
              >
                {header[0]}
              </div>
            );
          })}
        </div>
      )}
      {eventsList}
    </div>
  );
};

export default EventList;
